import React, { useState } from "react";
import { JssProvider, createUseStyles } from "react-jss";
import _ from "lodash";
import jssVariables from "../../../../jss_setup";
import {
  StyleTypeContext,
  TranslationContext,
} from "../../../../umbilical_tools";
import { useContext } from "react";
import { LoaderDesktop, LoaderMobile } from "../helpers";
import {
  useProductDetailsReservations,
  useProductReservations,
} from "../hooks";
import { Button } from "../../../../components/common/Button";
import { ViewDetailsModal, downloadActivityVoucher } from "./helpers";
import PropTypes from "prop-types";
import PaxDetails from "./PaxDetails";
import { getProductBasicData, getProductExtraData } from "./dataGetters";
import { sendReservationEmail } from "../../../../api/reservations";
import { toast } from "react-toastify";
import { InlineIcon } from "@iconify/react";
import { brand } from "../../../../context";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cancelReservation, requestCancelReservation } from "../../../../api";
import { useGuestProfile } from "../../../../hooks/profile";
import { useApoAuth } from "../../../../hooks";
import { useSelector } from "react-redux";
import { LedgerDetails } from "./LedgerDetails";

export const verticalTableStyles = {
  BasicData: { fontSize: "medium", smallscreen: { fontSize: "large" } },
  table: {
    width: "100%",
    borderCollapse: "unset",
    borderSpacing: `0 ${jssVariables.half_gap}`,
    "& tr": { verticalAlign: "baseline" },
    "& td": {
      padding: [jssVariables.half_gap, jssVariables.normal_gap],
      fontWeight: 500,
    },
  },
  tHeader: {
    background: "#F1F1F1",
    textTransform: "capitalize",
    color: jssVariables.colors.base_color,
    borderRadius: jssVariables.half_gap,
    width: "40%",
  },
};

const basicDataStyles = createUseStyles({
  BasicData: (props) => ({
    ...verticalTableStyles.BasicData,
    ...(props?.customStyles?.BasicData ?? {}),
  }),
  table: (props) => ({
    ...verticalTableStyles.table,
    ...(props?.customStyles?.table ?? {}),
  }),
  tHeader: (props) => ({
    ...verticalTableStyles.tHeader,
    ...(props?.customStyles?.tHeader ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    BasicData: (props) => ({
      ...verticalTableStyles.BasicData.smallscreen,
      ...(props?.customStyles?.BasicData?.smallscreen ?? {}),
    }),
    tHeader: (props) => ({
      width: "30%",
      ...(props?.customStyles?.tHeader?.smallscreen ?? {}),
    }),
  },
});
export const BasicData = ({ productType, reservation, children }) => {
  const { t } = useContext(TranslationContext);

  const classes = basicDataStyles({});

  const data = getProductBasicData({ productType, reservation, t });

  return (
    <div className={classes.BasicData}>
      <table className={classes.table}>
        <tbody>
          {data.map((field, idx) => (
            <tr key={idx}>
              <td className={classes.tHeader}>{field[0]}:</td>
              <td>{field[1]}</td>
            </tr>
          ))}
          {children}
        </tbody>
      </table>
    </div>
  );
};
BasicData.defaultProps = {
  productType: "ACC",
  reservation: {},
  children: null,
};
BasicData.propTypes = {
  productType: PropTypes.string.isRequired,
  reservation: PropTypes.object.isRequired,
  children: PropTypes.node,
};

const detailsStyles = createUseStyles({
  Details: (props) => ({
    padding: jssVariables.normal_gap,
    ...(props?.customStyles?.Details ?? {}),
  }),
  body: (props) => ({
    maxHeight: "50vh",
    overflowY: "auto",
    padding: jssVariables.normal_gap,
    display: "grid",
    gap: jssVariables.normal_gap,
    ...(props?.customStyles?.body ?? {}),
  }),
  attribute: (props) => ({
    ...(props?.customStyles?.attribute ?? {}),
  }),
  emptyRow: (props) => ({
    height: jssVariables.double_gap,
    ...(props?.customStyles?.emptyRow ?? {}),
  }),
  tHeader: (props) => ({
    ...verticalTableStyles.tHeader,
    ...(props?.customStyles?.tHeader ?? {}),
  }),
  emailActions: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    gridAutoFlow: "unset",
    gridAutoColumns: "unset",
    justifyContent: "center",
    gridGap: jssVariables.normal_gap,
    marginTop: jssVariables.normal_gap,
    ...(props?.customStyles?.emailActions ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    Details: (props) => ({
      ...(props?.customStyles?.Details?.smallscreen ?? {}),
    }),
    attribute: (props) => ({
      ...(props?.customStyles?.attribute?.smallscreen ?? {}),
    }),
    emailActions: (props) => ({
      gridAutoFlow: "column",
      gridAutoColumns: "max-content",
      gridTemplateColumns: "unset",
      ...(props?.customStyles?.emailActions ?? {}),
    }),
  },
});
export const Details = ({ productType, reservationRefId }) => {
  const queryClient = useQueryClient();

  const [mode, setMode] = useState("view"); // cancellation
  const [cxlData, setCxlData] = useState(null);

  const [loadingSendEmail, setLoadingSendEmail] = useState(false);
  const { reservation, loading } = useProductDetailsReservations(
    productType,
    reservationRefId
  );
  const guestprofile = useGuestProfile();
  const { user, website_api_key } = useApoAuth();

  const { t } = useContext(TranslationContext);

  const classes = detailsStyles({});

  const data = getProductExtraData({ productType, reservation, t });

  async function onSendEmail() {
    setLoadingSendEmail(true);
    var payload = {
      reference: reservation.reference,
      reservation_type: productType,
      market_type: reservation?.market_type ?? "B2C",
      email_type:
        reservation.status === "CO"
          ? "VO"
          : reservation.status === "CA"
          ? "RC"
          : reservation.status,
    };
    const response = await sendReservationEmail(payload);

    if (!response) {
      toast.error(t("emailNotSentTryAgain"), { autoClose: 5000 });
    } else {
      toast.success(t("emailHasBeenSent"), { autoClose: 5000 });
    }
    setLoadingSendEmail(false);
  }

  async function onDownloadVoucher({ reservation, productType }) {
    setLoadingSendEmail(true);
    switch (productType) {
      case "ACT":
        await downloadActivityVoucher({ reservation });
        break;
      default:
        break;
    }
    setLoadingSendEmail(false);
  }

  const requestCxlMutation = useMutation({
    mutationFn: ({
      user_id,
      website_api_key,
      reference,
      reservation_type,
      action_type,
    }) =>
      requestCancelReservation({
        user_id,
        website_api_key,
        reference,
        reservation_type,
        action_type,
      }),
    onSuccess: (data) => {
      setCxlData(data?.data);
      setMode("cancellation");
    },
    onError: () => {
      toast.error(
        "We couldn't proceed with requesting a reservation cancellation. Please reach out to the Travily Support Team for assistance with the cancellation process.",
        { autoClose: 5000 }
      );
    },
  });

  const executeCxlMutation = useMutation({
    mutationFn: ({ reference, reservation_type }) =>
      cancelReservation({ reference, reservation_type }),
    onSuccess: (___, variables) => {
      toast.success("Reservation has been successfully cancelled.", {
        autoClose: 5000,
      });
      setMode("view");
      setCxlData(null);
      queryClient.invalidateQueries([
        "reservation",
        variables.reference,
        variables.reservation_type,
      ]);
    },
    onError: (___, variables) => {
      toast.error(
        "This reservation could not be cancelled at this time. Please reach out to the Travily Support Team for assistance with the cancellation process.",
        {
          autoClose: 5000,
        }
      );
      setMode("view");
      setCxlData(null);
      queryClient.invalidateQueries([
        "reservation",
        variables.reference,
        variables.reservation_type,
      ]);
    },
  });

  const { hotel_booking_terms_label } = useSelector(
    (state) => state.WebsiteMetadata.metadata
  );

  return (
    <div className={classes.Details}>
      {mode === "view" ? (
        <React.Fragment>
          {loading || requestCxlMutation.isLoading || _.isEmpty(reservation) ? (
            <LoaderMobile />
          ) : (
            <div className={classes.body}>
              <BasicData productType={productType} reservation={reservation}>
                {data.map((field, idx) => (
                  <tr key={idx}>
                    <td className={classes.tHeader}>{field[0]}:</td>
                    <td>{field[1]}</td>
                  </tr>
                ))}
                {!!hotel_booking_terms_label && (
                  <tr>
                    <td className={classes.tHeader}>Disclaimer:</td>
                    <td>{hotel_booking_terms_label}</td>
                  </tr>
                )}
                <tr className={classes.emptyRow}></tr>
              </BasicData>
              <LedgerDetails
                productType={productType}
                productReference={reservation?.reference}
              />
              <PaxDetails productType={productType} reservation={reservation} />
            </div>
          )}
          <div className={classes.emailActions}>
            {reservation?.status === "CO" && brand !== "festival" && (
              <Button
                text="Cancel Reservation"
                onClick={() => {
                  const user_id = !!user ? user.id : guestprofile.ext_user_id;
                  const payload = {
                    user_id,
                    website_api_key,
                    reference: reservation.reference,
                    reservation_type: productType,
                    action_type: "CA",
                  };
                  requestCxlMutation.mutate(payload);
                }}
              />
            )}
            {loadingSendEmail ? (
              <Button
                text={
                  <InlineIcon
                    icon="eos-icons:loading"
                    style={{ fontSize: "x-large" }}
                  />
                }
                onClick={() => {}}
                customStyles={{ ButtonDefault: { width: "100%", gridGap: 0 } }}
              />
            ) : (
              <React.Fragment>
                {reservation?.status === "CO" && (
                  <React.Fragment>
                    <Button
                      text={t("sendVoucherEmail")}
                      onClick={() => onSendEmail()}
                    />
                    {productType === "ACT" && (
                      <Button
                        text={t("downloadVoucher")}
                        onClick={() =>
                          onDownloadVoucher({ reservation, productType })
                        }
                      />
                    )}
                  </React.Fragment>
                )}
                {reservation?.status === "CA" && (
                  <Button
                    text={t("sendCancellationEmail")}
                    onClick={() => onSendEmail()}
                  />
                )}
              </React.Fragment>
            )}
          </div>
        </React.Fragment>
      ) : mode === "cancellation" ? (
        executeCxlMutation.isLoading ? (
          <LoaderMobile />
        ) : (
          <div className={classes.body}>
            <p>
              {cxlData?.cancellation_request_info?.type === "refundable"
                ? "This booking is fully refundable. Upon completion of the cancellation process, your Travily Points will be credited back to your account."
                : "This reservation is non-refundable."}
            </p>
            <br />
            <div className="col2">
              <Button
                text={t("back")}
                onClick={() => {
                  setMode("view");
                  setCxlData(null);
                  queryClient.invalidateQueries([
                    "reservation",
                    reservationRefId,
                    productType,
                  ]);
                }}
              />
              {cxlData?.cancellation_request_info?.type === "refundable" && (
                <Button
                  text={t("proceed")}
                  onClick={() =>
                    executeCxlMutation.mutate({
                      reference: reservation.reference,
                      reservation_type: productType,
                    })
                  }
                />
              )}
            </div>
          </div>
        )
      ) : null}
    </div>
  );
};
Details.defaultProps = {
  productType: "ACC",
  reservationRefId: "",
};
Details.propTypes = {
  productType: PropTypes.string.isRequired,
  reservationRefId: PropTypes.string.isRequired,
};

const reservationsMobileStyles = createUseStyles({
  ReservationsMobile: (props) => ({
    ...(props?.customStyles?.ReservationsMobile ?? {}),
  }),
  header: (props) => ({
    fontSize: "large",
    fontWeight: "bold",
    ...(props?.customStyles?.header ?? {}),
  }),
  body: (props) => ({
    fontSize: "medium",
    ...(props?.customStyles?.body ?? {}),
  }),
  reservation: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    ...(props?.customStyles?.reservation ?? {}),
  }),
  divider: (props) => ({
    height: "1px",
    backgroundColor: jssVariables.colors.border,
    margin: [jssVariables.normal_gap, 0],
    ...(props?.customStyles?.divider ?? {}),
  }),
});
export const ReservationsMobile = ({
  reservations,
  loading,
  productType,
  onViewDetails,
}) => {
  const classes = reservationsMobileStyles();
  const { t } = useContext(TranslationContext);

  return (
    <div className={classes.ReservationsMobile}>
      <div className={classes.body}>
        {loading ? (
          new Array(3).fill(1).map((x, idx) => (
            <React.Fragment key={idx}>
              <LoaderMobile />
              {idx !== 2 && <div className={classes.divider} />}
            </React.Fragment>
          ))
        ) : reservations.length > 0 ? (
          reservations.map((reservation, idx) => (
            <div className={classes.reservation} key={idx}>
              <BasicData productType={productType} reservation={reservation} />
              <Button
                text={t("viewMoreDetails")}
                onClick={() => onViewDetails(reservation.reference)}
              />
              {idx !== reservations.length - 1 && (
                <div className={classes.divider} />
              )}
            </div>
          ))
        ) : (
          <div className={classes.attribute}>
            <strong>{t("noReservationsForThisProduct")}</strong>
          </div>
        )}
      </div>
    </div>
  );
};
ReservationsMobile.defaultProps = {
  productType: "ACC",
  reservations: [],
  loading: false,
};
ReservationsMobile.propTypes = {
  productType: PropTypes.string.isRequired,
  reservations: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  onViewDetails: PropTypes.func.isRequired,
};

const reservationsDesktopStyles = createUseStyles({
  ReservationsDesktop: (props) => ({
    ...(props?.customStyles?.ReservationsDesktop ?? {}),
  }),
  table: (props) => ({
    width: "100%",
    "& td:not(:first-child)": { borderLeft: "1px solid #cccccc" },
    "& th": { padding: jssVariables.half_gap },
    "& td": {
      padding: [jssVariables.half_gap, jssVariables.normal_gap],
      color: jssVariables.colors.text.dark,
      fontSize: "medium",
      textAlign: "center",
      maxWidth: "9rem",
    },
    ...(props?.customStyles?.table ?? {}),
  }),
  thead: (props) => ({
    background: "#F7F7F7",
    textTransform: "capitalize",
    fontWeight: 500,
    color: jssVariables.colors.base_color,
    padding: jssVariables.half_gap,
    ...(props?.customStyles?.thead ?? {}),
  }),
  emptyDivider: (props) => ({
    height: jssVariables.normal_gap,
    width: "100%",
    background: "transparent",
    ...(props?.customStyles?.emptyDivider ?? {}),
  }),
  dateValue: (props) => ({
    whiteSpace: "nowrap",
    ...(props?.customStyles?.dateValue ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    ReservationsDesktop: (props) => ({
      ...(props?.customStyles?.ReservationsDesktop?.smallscreen ?? {}),
    }),
  },
});
export const ReservationsDesktop = ({
  reservations,
  loading,
  productType,
  onViewDetails,
}) => {
  const classes = reservationsDesktopStyles({});
  const { t } = useContext(TranslationContext);

  return (
    <div className={classes.ReservationsDesktop}>
      {loading ? (
        <LoaderDesktop />
      ) : reservations.length > 0 ? (
        <table className={classes.table}>
          <thead className={classes.thead}>
            <tr>
              {getProductBasicData({
                productType,
                reservation: reservations[0],
                t,
              }).map((datum, idx) => (
                <th key={idx}>{datum[0]}</th>
              ))}
              <th>{t("actions")}</th>
            </tr>
          </thead>
          <tbody className={classes.tbody}>
            <tr className={classes.emptyDivider} />
            {reservations.map((reservation, idx) => (
              <tr key={idx}>
                {getProductBasicData({
                  productType,
                  reservation,
                  t,
                }).map((datum, index) => (
                  <td key={index}>{datum[1]}</td>
                ))}
                <td>
                  <JssProvider
                    classNamePrefix={`reservation___${idx}-`}
                    key={idx}>
                    <Button
                      text={t("viewMoreDetails")}
                      size="small"
                      revert={true}
                      empty={true}
                      onClick={() => onViewDetails(reservation.reference)}
                      customStyles={{
                        ButtonDefault: {
                          padding: `0 ${jssVariables.double_gap}`,
                        },
                      }}
                    />
                  </JssProvider>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <strong>{t("noBookingsForThisProduct")}</strong>
      )}
    </div>
  );
};
ReservationsDesktop.defaultProps = {
  productType: "ACC",
  reservations: [],
  loading: false,
};
ReservationsDesktop.propTypes = {
  productType: PropTypes.string.isRequired,
  reservations: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  onViewDetails: PropTypes.func.isRequired,
};

const styles = createUseStyles({
  ProductReservations: (props) => ({
    position: "relative",
    padding: [0, jssVariables.normal_gap],
    ...(props?.customStyles?.ProductReservations ?? {}),
  }),
  reservationsDesktop: { display: "none" },
  reservationsMobile: { display: "block" },
  [`@media ${jssVariables.media.smallscreen}`]: {
    ProductReservations: (props) => ({
      padding: 0,
      ...(props?.customStyles?.ProductReservations?.smallscreen ?? {}),
    }),
    reservationsDesktop: { display: "block" },
    reservationsMobile: { display: "none" },
  },
});
export const ProductReservations = ({ productType }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [reservationRefId, setReservationRefId] = useState(null);

  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const classes = styles({
    customStyles: cStyler({ type, brand, objName: "ProductReservations" }),
  });

  const { reservations, loading } = useProductReservations(
    productType,
    showDetailsModal
  );

  function showDetails(id) {
    setShowDetailsModal(true);
    setReservationRefId(id);
  }

  return (
    <div className={classes.ProductReservations}>
      <div className={classes.reservationsDesktop}>
        <ReservationsDesktop
          reservations={reservations}
          loading={loading}
          productType={productType}
          onViewDetails={(id) => showDetails(id)}
        />
      </div>
      <div className={classes.reservationsMobile}>
        <ReservationsMobile
          reservations={reservations}
          loading={loading}
          productType={productType}
          onViewDetails={(id) => showDetails(id)}
        />
      </div>
      {showDetailsModal && (
        <ViewDetailsModal
          productType={productType}
          onHide={() => {
            setShowDetailsModal(false);
            setReservationRefId(null);
          }}>
          <Details
            productType={productType}
            reservationRefId={reservationRefId}
          />
        </ViewDetailsModal>
      )}
    </div>
  );
};
ProductReservations.defaultProps = { productType: "ACC" };
ProductReservations.propTypes = { productType: PropTypes.string.isRequired };
